import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Snackbar,
  MenuItem,
  DialogContentText,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import { API_ENDPOINT } from "../../utils/constants";
import { getToken } from "../../utils/utils2";

// Unit multipliers to convert everything to minutes for the backend
const UNIT_MULTIPLIERS = {
  m: 1,
  h: 60,
  d: 1440, // 24 * 60
  w: 10080, // 7 * 24 * 60
  mo: 43200, // 30 * 24 * 60 (Approx)
  y: 525600, // 365 * 24 * 60
};

const OnsiteLinks = () => {
  // ----------------------------------------------------
  // STATE MANAGEMENT
  // ----------------------------------------------------
  // Generate Modal State
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [timeValue, setTimeValue] = useState(1);
  const [timeUnit, setTimeUnit] = useState("d");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateErrorMsg, setGenerateErrorMsg] = useState("");

  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteErrorMsg, setDeleteErrorMsg] = useState("");

  // Snackbar State (for copy feedback)
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // ----------------------------------------------------
  // DATA FETCHING
  // ----------------------------------------------------
  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/admin/get-onsite-links`
  );

  // Utility to generate the full frontend link based on the token
  const getFullLink = (token) =>
    `${window.location.origin}/onsite-module/${token}`;

  // ----------------------------------------------------
  // GENERATE LINK HANDLERS
  // ----------------------------------------------------
  const handleOpenGenerateModal = () => {
    setTimeValue(1);
    setTimeUnit("d");
    setGenerateErrorMsg("");
    setGenerateModalOpen(true);
  };

  const handleCloseGenerateModal = () => {
    setGenerateModalOpen(false);
  };

  const applyPreset = (value, unit) => {
    setTimeValue(value);
    setTimeUnit(unit);
  };

  const handleGenerateLink = async () => {
    if (!timeValue || timeValue <= 0) return;

    setIsGenerating(true);
    setGenerateErrorMsg("");

    const durationInMinutes = timeValue * UNIT_MULTIPLIERS[timeUnit];

    try {
      const res = await fetch(`${API_ENDPOINT}/admin/generate-onsite-link`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ duration: durationInMinutes }),
      });

      if (res.ok) {
        refetch();
        handleCloseGenerateModal();
        showSnackbar("Link generated successfully!");
      } else {
        const errData = await res.json();
        setGenerateErrorMsg(errData.message || "Failed to generate link");
      }
    } catch (error) {
      console.error("Error generating link:", error);
      setGenerateErrorMsg("An unexpected error occurred while generating.");
    } finally {
      setIsGenerating(false);
    }
  };

  // ----------------------------------------------------
  // DELETE LINK HANDLERS
  // ----------------------------------------------------
  const handleOpenDeleteModal = (token) => {
    setLinkToDelete(token);
    setDeleteErrorMsg("");
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setLinkToDelete(null);
    setDeleteModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!linkToDelete) return;
    setIsDeleting(true);
    setDeleteErrorMsg("");

    try {
      const res = await fetch(
        `${API_ENDPOINT}/admin/delete-onsite-link/${linkToDelete}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      if (res.ok) {
        refetch();
        handleCloseDeleteModal();
        showSnackbar("Link deleted successfully!");
      } else {
        const errData = await res.json();
        setDeleteErrorMsg(errData.message || "Failed to delete link");
      }
    } catch (error) {
      console.error("Error deleting link:", error);
      setDeleteErrorMsg("An unexpected error occurred while deleting.");
    } finally {
      setIsDeleting(false);
    }
  };

  // ----------------------------------------------------
  // ACTION BUTTON HANDLERS
  // ----------------------------------------------------
  const handleCopyLink = (token) => {
    if (token) {
      navigator.clipboard.writeText(getFullLink(token));
      showSnackbar("Link copied to clipboard!");
    }
  };

  const handleOpenLink = (token) => {
    if (token) {
      window.open(getFullLink(token), "_blank", "noopener,noreferrer");
    }
  };

  const showSnackbar = (msg) => {
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  };

  // ----------------------------------------------------
  // RENDER HELPERS
  // ----------------------------------------------------
  if (isLoading) {
    return <MuPb />;
  }

  if (isError) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">Error loading links: {error?.message}</Alert>
      </Container>
    );
  }

  const links = data?.data || [];
  const formatIST = (date) =>
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
      .format(new Date(date))
      .replace(",", ",")
      .replace(" am", "AM")
      .replace(" pm", "PM");

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Area */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h4">Onsite Module</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenGenerateModal}
        >
          Generate New Link
        </Button>
      </Box>

      {/* Table Section */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Module</TableCell>
              <TableCell>Created On</TableCell>
              <TableCell>Expires At</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {links.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    No links generated yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              links.map((link, idx) => (
                <TableRow key={idx} hover>
                  <TableCell>Onsite Module</TableCell>
                  <TableCell>{formatIST(link.createdOn)}</TableCell>
                  <TableCell>{formatIST(link.expiresAt)}</TableCell>
                  <TableCell>
                    <Chip
                      label={link.isExpired ? "Expired" : "Active"}
                      color={link.isExpired ? "error" : "success"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Copy Link">
                      <IconButton
                        color="primary"
                        onClick={() => handleCopyLink(link.token)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open in New Tab">
                      <IconButton
                        color="secondary"
                        onClick={() => handleOpenLink(link.token)}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Link">
                      <IconButton
                        color="error"
                        onClick={() => handleOpenDeleteModal(link.token)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ---------------------------------------------------- */}
      {/* GENERATE LINK MODAL */}
      {/* ---------------------------------------------------- */}
      <Dialog
        open={generateModalOpen}
        onClose={handleCloseGenerateModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Generate Onsite Link</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Specify how long the module link should remain active.
          </DialogContentText>

          {generateErrorMsg && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {generateErrorMsg}
            </Alert>
          )}

          {/* Presets */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
            <Chip
              label="1 Day"
              onClick={() => applyPreset(1, "d")}
              variant={
                timeValue === 1 && timeUnit === "d" ? "filled" : "outlined"
              }
              color="primary"
            />
            <Chip
              label="1 Week"
              onClick={() => applyPreset(1, "w")}
              variant={
                timeValue === 1 && timeUnit === "w" ? "filled" : "outlined"
              }
              color="primary"
            />
            <Chip
              label="1 Month"
              onClick={() => applyPreset(1, "mo")}
              variant={
                timeValue === 1 && timeUnit === "mo" ? "filled" : "outlined"
              }
              color="primary"
            />
            <Chip
              label="1 Year"
              onClick={() => applyPreset(1, "y")}
              variant={
                timeValue === 1 && timeUnit === "y" ? "filled" : "outlined"
              }
              color="primary"
            />
          </Box>

          {/* Custom Input */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Duration"
              type="number"
              fullWidth
              value={timeValue}
              onChange={(e) => setTimeValue(e.target.value)}
              disabled={isGenerating}
              inputProps={{ min: 1 }}
            />
            <TextField
              select
              label="Unit"
              value={timeUnit}
              onChange={(e) => setTimeUnit(e.target.value)}
              disabled={isGenerating}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="m">Minutes</MenuItem>
              <MenuItem value="h">Hours</MenuItem>
              <MenuItem value="d">Days</MenuItem>
              <MenuItem value="w">Weeks</MenuItem>
              <MenuItem value="mo">Months</MenuItem>
              <MenuItem value="y">Years</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseGenerateModal} disabled={isGenerating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateLink}
            disabled={isGenerating}
          >
            {isGenerating ? "Generating..." : "Generate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------------------------------------------- */}
      {/* DELETE CONFIRMATION MODAL */}
      {/* ---------------------------------------------------- */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: "error.main" }}>Delete Link</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this link? Anyone currently using it
            will lose access immediately. This action cannot be undone.
          </DialogContentText>

          {deleteErrorMsg && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteErrorMsg}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDeleteModal} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------------------------------------------- */}
      {/* GLOBAL SNACKBAR (Feedback) */}
      {/* ---------------------------------------------------- */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Container>
  );
};

export default OnsiteLinks;
